import React, { useEffect, useRef, useState } from 'react';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useAuth } from '../contexts/AuthContext';
import { cleanupLegacyStorage } from '../utils/userStorage';
import '../anima/Splash/src/screens/CcSplash/style.css';
import '../anima/Splash/styleguide.css';
import { CcSplash as SplashComponent } from '../anima/Splash/src/screens/CcSplash';
import { storage } from '../services/storage';
import { API_BASE_URL } from '../utils/api'; // Environment-aware API URL
import { Platform } from 'react-native'; // Mission 20: Platform detection
import {
  resolveOnboardingState,
  type OnboardingState,
} from '../services/onboardingStateResolver';

const Splash: React.FC = () => {
  const router = useRouter();
  const searchParams = useLocalSearchParams<{ token?: string }>();
  const { user, isLoading, login } = useAuth();

  const timerRef = useRef<number | null>(null);
  const progressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (__DEV__) console.log('🎬 SPLASH: useEffect triggered:', {
      user: user?.id,
      isLoading,
      searchParams: JSON.stringify(searchParams),
      pathname: Platform.OS === 'web' ? window.location.pathname : 'mobile-app'
    });

    // Don't do anything while still loading
    if (isLoading) {
      if (__DEV__) console.log('⏳ SPLASH: Still loading, waiting...');
      return;
    }

    // CRITICAL: Don't interfere with Auth0 callback processing (web only)
    if (Platform.OS === 'web' && window.location.pathname.includes('/auth/callback')) {
      if (__DEV__) console.log('🚫 Splash: Skipping redirect logic during Auth0 callback processing');
      return;
    }

    // Check for invite token in URL params first
    const rawToken = searchParams.token as string | undefined;

    // Handle malformed URLs where route gets mixed into token (e.g., 'test-user-1/chat')
    let token = rawToken;
    if (rawToken && rawToken.includes('/')) {
      const parts = rawToken.split('/');
      if (parts[0].startsWith('test-user-')) {
        token = parts[0]; // Extract just the test user ID
        if (__DEV__) console.log('🔧 Splash - Fixed malformed token:', { original: rawToken, fixed: token, route: parts[1] });
      }
    }

    // Handle authentication token
    if (token && !user) {
      if (__DEV__) console.log('🎯 Handling authentication token flow');

      // Clean up any legacy non-user-specific localStorage data
      cleanupLegacyStorage();

      if (__DEV__) console.log('🔍 Token authentication:', {
        token,
        isTestUser: token.startsWith('test-user-'),
        hasUser: !!user,
        isLoading
      });

      // For test users, authenticate immediately and let ProtectedRoute handle routing
      if (token.startsWith('test-user-')) {
        if (__DEV__) console.log('🔐 Test user - authenticating immediately');
        handleInviteToken(token);
        return;
      }

      // For invite tokens, proceed with backend authentication
      if (token.startsWith('invite_')) {
        if (__DEV__) console.log('🎫 Real invite token - validating with backend');
        handleInviteToken(token);
        return;
      }

      // For Paul's production testing keys, proceed with backend authentication
      if (token.startsWith('paul_')) {
        if (__DEV__) console.log('🎫 Paul production token - validating with backend');
        handleInviteToken(token);
        return;
      }

      // For other tokens, proceed with normal authentication
      handleInviteToken(token);
      return;
    }

    // Check if user is already authenticated - check their completion status FROM BACKEND
    if (user) {
      if (__DEV__) console.log('🔑 SPLASH: User already authenticated, checking completion status from backend:', user.id);
      if (__DEV__) console.log('🔍 SPLASH: User object:', JSON.stringify(user, null, 2));
      if (__DEV__) console.log('🔍 SPLASH: Current route:', Platform.OS === 'web' ? window.location.pathname : 'mobile-app');

      // DON'T start progress animation yet - wait until we know where we're routing

      // Async function to fetch user state.
      //
      // WELCOME-SCREEN REGRESSION FIX (2026-05-20):
      // Pre-fix, this function used a plain fetch with a fallback that
      // routed to /onboarding on ANY non-2xx (and on network errors after
      // retries). During an ALB 5xx storm on 2026-05-20, established
      // users hit this branch and were sent back to the welcome letter.
      // Now delegates fetch + cache + cascade to the shared resolver
      // (see web/src/services/onboardingStateResolver.ts). The 401-retry
      // loop is preserved here to handle the Auth0 cold-start race
      // (token isn't ready immediately after web sign-in).
      const checkUserState = async (retryCount = 0, maxRetries = 5) => {
        if (__DEV__) console.log('🔄 SPLASH: checkUserState called (retry:', retryCount, `of ${maxRetries})`);

        const jwtToken = await storage.getItem('jwt_token');
        if (!jwtToken) {
          if (__DEV__) console.warn('⚠️ No JWT token found, routing to onboarding');
          startProgressAnimation();
          setTimeout(() => router.replace('/onboarding'), 3000);
          return;
        }

        if (!user?.id) {
          // Same defensive guard as web/src/app/index.tsx — refuse to
          // run user-keyed lookups with no userId.
          if (__DEV__) console.warn('⚠️ SPLASH: No user.id available, routing to onboarding');
          startProgressAnimation();
          setTimeout(() => router.replace('/onboarding'), 3000);
          return;
        }

        const routeWithAnimation = (destination: string) => {
          startProgressAnimation();
          timerRef.current = window.setTimeout(() => {
            router.replace(destination);
          }, 3000);
        };

        try {
          const result = await resolveOnboardingState(
            API_BASE_URL,
            jwtToken,
            user.id,
          );

          if (result.kind === 'unauthorized') {
            // Auth0 cold-start race: the JWT may not be ready yet on
            // web sign-in. Retry with backoff, then fall through to
            // onboarding if Auth0 still can't validate the token.
            if (retryCount < maxRetries) {
              const waitTime = 1000 * (retryCount + 1);
              if (__DEV__) console.warn(`⚠️ JWT token rejected (401), waiting ${waitTime}ms for Auth0 token refresh (attempt ${retryCount + 1}/${maxRetries})...`);
              if (!mountedRef.current) return;
              setTimeout(() => {
                if (!mountedRef.current) return;
                checkUserState(retryCount + 1, maxRetries);
              }, waitTime);
              return;
            }
            if (__DEV__) console.warn('⚠️ Token still rejected after retries, routing to onboarding');
            routeWithAnimation('/onboarding');
            return;
          }

          if (result.kind === 'positive_signal') {
            if (__DEV__) console.log(`✅ SPLASH: positive signal (${result.signal}) — routing to CHAT`);
            routeWithAnimation('/chat');
            return;
          }

          if (result.kind === 'no_signal') {
            if (__DEV__) console.warn('⚠️ SPLASH: No state, no cache, no positive signal — routing to ONBOARDING');
            routeWithAnimation('/onboarding');
            return;
          }

          // `fresh` or `cached` — both carry an authoritative state.
          // Cached state is safe because onboarding state is monotonic.
          const state: OnboardingState = result.state;
          if (result.kind === 'cached' && __DEV__) {
            console.log(`📦 SPLASH: using cached state (age ${Math.round(result.ageMs / 1000)}s)`);
          }

          // Route based on backend state. Same conditional cascade as
          // pre-fix — only the data source changed (resolver vs. inline
          // fetch). Web routes use flat paths (no (auth) / (tabs) groups).
          let destination = '/onboarding';
          if (state.has_accepted_legal) {
            destination = '/chat';
            if (__DEV__) console.log('✅ SPLASH: Legal accepted - routing to CHAT');
          } else if (state.tour_completed || state.onboarding_step >= 7) {
            destination = '/legal';
            if (__DEV__) console.log('⚠️ SPLASH: Tour completed but legal not accepted - routing to LEGAL');
          } else {
            if (__DEV__) {
              console.log('⚠️ SPLASH: Onboarding not completed - routing to ONBOARDING');
              console.log('⚠️ SPLASH: tour_completed:', state.tour_completed, 'onboarding_step:', state.onboarding_step);
            }
          }

          if (__DEV__) console.log('🎯 Authenticated user routing decision:', {
            userId: user.id,
            onboarding_step: state.onboarding_step,
            tour_completed: state.tour_completed,
            has_accepted_legal: state.has_accepted_legal,
            kind: result.kind,
            destination,
          });

          routeWithAnimation(destination);
        } catch (error) {
          // The resolver only throws AbortError. Anything else here is
          // a network error inside the resolver's own helpers.
          if (__DEV__) console.error('❌ SPLASH: resolver error:', error);

          // Retry on network errors (Auth0 might still be loading) —
          // preserves pre-fix behavior for slow cold-start networks.
          if (retryCount < maxRetries) {
            const waitTime = 1000 * (retryCount + 1);
            if (__DEV__) console.warn(`⚠️ Network error, retrying in ${waitTime}ms (attempt ${retryCount + 1}/${maxRetries})...`);
            if (!mountedRef.current) return;
            setTimeout(() => {
              if (!mountedRef.current) return;
              checkUserState(retryCount + 1, maxRetries);
            }, waitTime);
          } else {
            if (__DEV__) console.error('❌ SPLASH: failed after', maxRetries, 'retries, routing to onboarding');
            routeWithAnimation('/onboarding');
          }
        }
      };

      // Call the async function
      checkUserState();
      return;
    }

    // If no token and no user - let AuthGuard handle showing login screen
    if (!user && !token) {
      if (__DEV__) console.log('🔐 SPLASH: No authentication yet - waiting for AuthGuard/EnhancedAuthGuard');
      if (__DEV__) console.log('🔍 SPLASH: Will wait for user to be set by authentication system');
      // Don't navigate - let AuthGuard show Auth0LoginScreen or handle silent auth
      return;
    }

    if (__DEV__) console.log('⚠️ SPLASH: Reached end of useEffect without taking action', {
      hasUser: !!user,
      hasToken: !!token,
      isLoading
    });

  }, [user, isLoading, searchParams, router]);

  const handleInviteToken = async (token: string) => {
    if (__DEV__) console.log('🚀 handleInviteToken called:', { token, currentUser: user?.id });

    try {
      // Authenticate all valid tokens immediately
      if (token.startsWith('test-user-')) {
        if (__DEV__) console.log('🔐 Authenticating test user immediately:', { token });

        const testUser = {
          id: token,
          name: `Test User ${token.replace('test-user-', '')}`,
          email: `${token}@test.coachcedar.com`
        };

        await login(testUser, true); // skipDeviceRegistration = true for test users
        if (__DEV__) console.log('✅ Test user authenticated successfully');

        // After authentication, check completion status and route appropriately
        const completionKey = `${token}_cedar_onboarding_completed`;
        const hasCompletedOnboarding = await storage.getItem(completionKey) === 'true';

        if (__DEV__) console.log('🎯 Post-authentication routing decision:', {
          token,
          hasCompletedOnboarding,
          destination: hasCompletedOnboarding ? '/chat' : '/onboarding'
        });

        // Start progress animation and navigate based on completion status
        startProgressAnimation();
        timerRef.current = window.setTimeout(() => {
          if (hasCompletedOnboarding) {
            router.replace('/chat');
          } else {
            router.replace('/onboarding');
          }
        }, 3000); // Reduced from 5000 to 3000ms
        return;
      }

      // For real invite tokens or Paul's production tokens, validate with backend using proper device binding
      if (token.startsWith('invite_') || token.startsWith('paul_')) {
        if (__DEV__) console.log('🎫 Validating token with backend:', { token });

        // **TESTING BYPASS**: Only available in development builds
        if (__DEV__ && (token === 'invite_r9gfkhhbg' || token === 'invite_jwt31gmcl' || token === 'invite_1a5acewvw' || token === 'invite_ekgo55poq' || token === 'invite_4x54fifps' || token === 'invite_paul_fresh_start')) {
          if (__DEV__) console.log('🚨 DEV BYPASS: Skipping device validation for testing token');

          try {
            const API_BASE = '/api';

            const directResponse = await fetch(`/api/validate-invite/${token}`);
            if (directResponse.ok) {
              const directData = await directResponse.json();
              if (__DEV__) console.log('✅ EMERGENCY BYPASS: Direct validation successful:', directData);

              // Create user from direct validation
              const bypassUser = {
                id: directData.id || token,
                name: `${directData.firstName || 'Test'} ${directData.lastName || 'User'}`,
                email: directData.email || 'test@coachcedar.com',
                isInviteUser: true,
                bypassDeviceBinding: true
              };

              // Store JWT token if available
              if (directData.jwtToken) {
                await storage.setItem('jwt_token', directData.jwtToken);
              }

              // **CRITICAL FIX**: Store user name for chat personalization
              const fullName = `${directData.firstName || ''} ${directData.lastName || ''}`.trim();
              if (fullName) {
                if (__DEV__) console.log('👤 Storing user name from bypass:', fullName);
                await storage.setItem('userName', fullName);
              }

              // **CRITICAL FIX**: Store invite token for later fallback authentication
              if (__DEV__) console.log('💾 Storing invite token for fallback authentication');
              await storage.setItem('invite_token', token);

              await login(bypassUser, true); // skipDeviceRegistration = true for bypass auth
              if (__DEV__) console.log('🎉 EMERGENCY BYPASS: Authentication successful');

              // Navigate to onboarding
              startProgressAnimation();
              timerRef.current = window.setTimeout(() => {
                router.replace('/onboarding');
              }, 3000);
              return;
            }
          } catch (emergencyError) {
            if (__DEV__) console.error('❌ EMERGENCY BYPASS also failed:', emergencyError);
            // Fall through to normal validation
          }
        }

        try {
          // Generate device fingerprint for binding
          const { generateDeviceFingerprint } = await import('../utils/deviceFingerprint');
          const deviceFingerprint = await generateDeviceFingerprint();

          // Call backend to validate invite token with device binding
          const API_BASE = `${API_BASE_URL}/api`;

          if (__DEV__) console.log('🔄 Validating with device fingerprint:', { API_BASE, deviceFingerprint });

          const response = await fetch(`${API_BASE}/admin/users/validate-invite`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              token: token,
              deviceFingerprint: deviceFingerprint
            }),
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || `Invalid invite token: ${response.status}`);
          }

          const validationResult = await response.json();
          if (__DEV__) console.log('✅ Token validation result:', validationResult);

          if (!validationResult.valid) {
            throw new Error(validationResult.error || 'Token validation failed');
          }

          const userData = validationResult.user;

          // **CRITICAL FIX**: Get JWT token from backend for LLM API authentication
          if (__DEV__) console.log('🔑 Getting JWT token for LLM authentication...');
          const jwtResponse = await fetch(`${API_BASE}/validate-invite/${token}`);
          if (jwtResponse.ok) {
            const jwtData = await jwtResponse.json();
            if (jwtData.jwtToken) {
              if (__DEV__) console.log('✅ JWT token received - storing for LLM API access');
              await storage.setItem('jwt_token', jwtData.jwtToken);

              // **CRITICAL FIX**: Store user name for chat personalization
              const fullName = `${jwtData.firstName || ''} ${jwtData.lastName || ''}`.trim();
              if (fullName) {
                if (__DEV__) console.log('👤 Storing user name:', fullName);
                await storage.setItem('userName', fullName);
              }

              // **CRITICAL FIX**: Store invite token for later fallback authentication
              if (__DEV__) console.log('💾 Storing invite token for fallback authentication');
              await storage.setItem('invite_token', token);
            } else {
              if (__DEV__) console.warn('⚠️ No JWT token in response - user may have authentication issues');
            }
          } else {
            const errorText = await jwtResponse.text();
            if (__DEV__) {
              console.error('❌ JWT token endpoint failed:', jwtResponse.status, errorText);
            } else {
              console.error('JWT token endpoint failed');
            }
            throw new Error(`Failed to get authentication token: ${jwtResponse.status}`);
          }

          // Mark user as registered with backend
          if (__DEV__) console.log('📝 Marking user as registered...');
          const markResponse = await fetch(`${API_BASE}/admin/users/mark-registered`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ token: token }),
          });

          if (!markResponse.ok) {
            if (__DEV__) console.warn('⚠️ Failed to mark user as registered, but continuing...');
          }

          // Create user object from backend response
          const inviteUser = {
            id: userData.id || token,
            name: `${userData.firstName} ${userData.lastName}`,
            email: userData.email,
            isInviteUser: true,
            deviceFingerprint: deviceFingerprint
          };

          await login(inviteUser, true); // skipDeviceRegistration = true for invite tokens
          if (__DEV__) console.log('🎉 Token authentication successful');

          // For invite users, always start with onboarding (they're new)
          if (__DEV__) console.log('🎯 Routing user to onboarding (new user flow)');

          // Start progress animation and navigate to onboarding
          startProgressAnimation();
          timerRef.current = window.setTimeout(() => {
            router.replace('/onboarding');
          }, 3000);
          return;

        } catch (error) {
          const errorMsg = error instanceof Error ? error.message : String(error);
          if (__DEV__) {
            console.error('❌ Invite token validation failed:', error);
            console.error('Full error details:', {
              message: errorMsg,
              token: token,
              apiBase: '/api',
              timestamp: new Date().toISOString(),
              userAgent: navigator.userAgent,
              isIncognito: Platform.OS === 'web' ? (window.navigator.webdriver !== undefined || window.outerHeight === 0) : false
            });
          } else {
            console.error('Token validation failed');
          }

          // Show more helpful error message
          if (errorMsg.includes('bound to a different device')) {
            if (__DEV__) console.log('🔄 Device binding conflict - this token may have been used before');

            // **TESTING BYPASS**: Only in development builds
            if (__DEV__ && (token === 'invite_r9gfkhhbg' || token === 'invite_jwt31gmcl' || token === 'invite_1a5acewvw' || token === 'invite_ekgo55poq' || token === 'invite_4x54fifps' || token === 'invite_paul_fresh_start')) {
              if (__DEV__) console.log('🔧 DEV BYPASS: Attempting direct validation for testing token');

              try {
                // Try the direct validation endpoint that doesn't require device binding
                const API_BASE = '/api';

                const directResponse = await fetch(`/api/validate-invite/${token}`);
                if (directResponse.ok) {
                  const directData = await directResponse.json();
                  if (__DEV__) console.log('✅ Direct validation successful:', directData);

                  // Create user from direct validation
                  const bypassUser = {
                    id: directData.id || token,
                    name: `${directData.firstName || 'Test'} ${directData.lastName || 'User'}`,
                    email: directData.email || 'test@coachcedar.com',
                    isInviteUser: true,
                    bypassDeviceBinding: true
                  };

                  // Store JWT token if available
                  if (directData.jwtToken) {
                    await storage.setItem('jwt_token', directData.jwtToken);
                  }

                  // **CRITICAL FIX**: Store user name for chat personalization
                  const fullName = `${directData.firstName || ''} ${directData.lastName || ''}`.trim();
                  if (fullName) {
                    if (__DEV__) console.log('👤 Storing user name from bypass:', fullName);
                    await storage.setItem('userName', fullName);
                  }

                  // **CRITICAL FIX**: Store invite token for later fallback authentication
                  if (__DEV__) console.log('💾 Storing invite token for fallback authentication');
                  await storage.setItem('invite_token', token);

                  await login(bypassUser, true); // skipDeviceRegistration = true for bypass auth
                  if (__DEV__) console.log('🎉 Bypass authentication successful');

                  // Navigate to onboarding
                  startProgressAnimation();
                  timerRef.current = window.setTimeout(() => {
                    router.replace('/onboarding');
                  }, 3000);
                  return;
                }
              } catch (bypassError) {
                if (__DEV__) console.error('❌ Bypass authentication also failed:', bypassError);
              }
            }
          } else if (errorMsg.includes('expired')) {
            if (__DEV__) console.log('⏰ Token has expired - please request a new invite');
          } else if (errorMsg.includes('already been used')) {
            if (__DEV__) console.log('✅ Token already used - please request a new invite');
          }

          // Token error - redirect to login screen for new authentication
          if (__DEV__) console.log('🔐 Token error - redirecting to login screen');
          router.replace('/login');
          return;
        }
      }

      // Unknown token format - reject
      if (__DEV__) console.error('Unknown token format:', token);
      router.replace('/login');
      return;
    } catch (error) {
      if (__DEV__) {
        console.error('❌ Token authentication failed:', error);
      } else {
        console.error('Token authentication failed');
      }
    }
  };

  // Separate function to handle progress animation
  const startProgressAnimation = () => {
    const duration = 3000; // 3 seconds (reduced from 5000)
    const interval = 50; // Update every 50ms
    const increment = 100 / (duration / interval);

    if (progressTimerRef.current) clearInterval(progressTimerRef.current);

    const timer = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev + increment;
        if (newProgress >= 100) {
          clearInterval(timer);
          return 100;
        }
        return newProgress;
      });
    }, interval);

    progressTimerRef.current = timer;
  };

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
      }
    };
  }, []);


  // Always show the splash component with progress
  return (
    <div
      className="fixed inset-0 w-full h-full overflow-hidden"
      style={{
        /* Bleed to all edges - ignore safe areas */
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        margin: 0,
        padding: 0
      }}
    >
      <SplashComponent progress={progress} />
    </div>
  );
};

export default Splash;
